/**
 * We should use the CoreBanner component from @clover/web-lib-core, but it needs some modifications to be fully useful to the Appmarket.
 * So for the moment this local appmarket component can be used. This component still uses color schemes and icons from the clover core
 */
import React, { useEffect, useState, useCallback } from 'react';
import styles from './styles.module.scss';
import { Typography } from '@material-ui/core';
import ctx from 'classnames';
import { useTranslation } from 'react-i18next';
import { CoreIcon } from '@clover/web-lib-core/components';
import { NotificationData, NotificationProperties, Notifications } from './types';

const DEFAULT_CLOSE_TIMER = 5000;

interface ComponentProps extends React.HTMLAttributes<HTMLDivElement> {
  closeCallBack?: () => void;
  notificationProperties?: NotificationProperties;
  notificationData: NotificationData;
}

let timeOutId: NodeJS.Timeout;

export const NotificationContainer: React.FC<ComponentProps> = (
  {
    closeCallBack,
    notificationProperties: {
      closeTimer,
      containerWidth,
      closeable = true,
      fixedPosition,
      gutterBottom,
    } = {},
    notificationData,
    className,
  }): Nullable<JSX.Element> => {
  const {body, variant, title} = notificationData;
  const selectedNotificationType = Notifications[variant];
  const { t } = useTranslation();
  const [isClosed, setClosed] = useState<boolean>(false);
  const titleArr = title.split('|');
  const error = titleArr.length > 1 ? (titleArr[0] || titleArr[1]) : undefined; //Notification thrown from api slice can have error and statusCode separated by |
  const width = containerWidth ? `${containerWidth}px` : 'none';

  const handleClose = useCallback(() => {
    setClosed(true);
    closeCallBack?.();
  }, [closeCallBack]);

  useEffect(() => {
    setClosed(false);
    if (closeTimer !== false) {
      clearTimeout(timeOutId);
      timeOutId = setTimeout(() => {
        handleClose();
      }, closeTimer || DEFAULT_CLOSE_TIMER);
    }
  }, [closeTimer, handleClose, notificationData]);

  if (isClosed) return null;

  return (
    <div
      className={ctx(
        styles.notification,
        selectedNotificationType.class,
        { [styles.fixedContainer]: fixedPosition },
        { [styles.gutterBottom]: gutterBottom },
        className,
      )}
      style={{ width: width }}
      role="alert"
    >
      <CoreIcon glyph={selectedNotificationType.icon} height={16} width={16} role="img" />
      <div className={styles.notificationText}>
        {title && (
          <Typography
            variant="body1"
            align="left"
            classes={{ root: ctx(styles.title, { [styles.boldTitle]: body }) }}
          >
            {error ? error.replace(':', '-') : t(title)}
          </Typography>
        )}
        {body && (
          <Typography variant="body1" align="left" data-testid='notificationBody'>
            {t(body)}
          </Typography>
        )}
      </div>
      {closeable && (
        <div className={styles.closeIcon}>
          <CoreIcon
            onClick={handleClose}
            glyph="close"
            height={14}
            width={14}
            role="application"
            data-testid="coreIcon"
          />
        </div>
      )}
    </div>
  );
};
